// Client for the Lender.Optimus.Integrations service (Integration Studio).
// Base host resolves from window.integrationsApiUrl (httpIntegrationsApi); CRUD +
// allowlist + run-history live under /api/integration-studio. Method execution
// (dry-run / live test) goes through LoanProcessing's test proxy
// (POST /api/integration-methods/execute), which resolves the loan context the
// request mapping reads — the same ActionContextInputs a loan-action run gets —
// before forwarding to the egress executor.

const STUDIO = '/api/integration-studio';
const PROVIDERS = `${STUDIO}/providers`;

export class IntegrationStudioApi {
  #http;

  #loanProcessingHttp;

  constructor(http, loanProcessingHttp) {
    this.#http = http;
    this.#loanProcessingHttp = loanProcessingHttp;
  }

  // --- Providers (Lender Integrations — first-party provider framework) ---

  async getProviders(config) {
    return this.#http.get(PROVIDERS, { ...config, skipDeduplication: true });
  }

  async getProviderConfig(providerKey) {
    return this.#http.get(`${PROVIDERS}/${providerKey}/config`, { skipDeduplication: true });
  }

  async updateProviderConfig(providerKey, settings) {
    return this.#http.put(`${PROVIDERS}/${providerKey}/config`, { settings });
  }

  async enableProvider(providerKey) {
    return this.#http.post(`${PROVIDERS}/${providerKey}/enable`);
  }

  async disableProvider(providerKey) {
    return this.#http.post(`${PROVIDERS}/${providerKey}/disable`);
  }

  async setProviderCredential(providerKey, credentialKey, value) {
    return this.#http.post(`${PROVIDERS}/${providerKey}/credentials`, {
      credentialKey,
      value,
    });
  }

  async rotateProviderCredential(providerKey, credentialId, value) {
    return this.#http.post(`${PROVIDERS}/${providerKey}/credentials/${credentialId}/rotate`, {
      value,
    });
  }

  async deleteProviderCredential(providerKey, credentialKey) {
    return this.#http.delete(`${PROVIDERS}/${providerKey}/credentials/${credentialKey}`);
  }

  // The choices of a select setting that lists the company's own records (channels, sub-companies, loan officers,
  // tags, document types) — resolved by the Integrations service for the signed-in company.
  async getProviderSettingOptions(providerKey, fieldKey) {
    return this.#http.get(`${PROVIDERS}/${providerKey}/setting-options/${fieldKey}`, {
      skipDeduplication: true,
    });
  }

  async testProviderConnection(providerKey) {
    return this.#http.post(`${PROVIDERS}/${providerKey}/test-connection`);
  }

  // The host keys an SFTP provider's server presents, for the settings form to trust. The body carries the form's
  // unsaved host, port, environment (MERS derives its host from it) and pins — any of them may be left out to use the
  // saved settings; nothing is saved.
  async fetchProviderHostKeys(providerKey, { host, port, environment, hostKeyFingerprint } = {}) {
    return this.#http.post(`${PROVIDERS}/${providerKey}/host-key/fetch`, {
      host: host || null,
      port: port ?? null,
      environment: environment || null,
      hostKeyFingerprint: hostKeyFingerprint ?? null,
    });
  }

  // --- Packages ---

  async getStats() {
    return this.#http.get(`${STUDIO}/stats`);
  }

  async getPackages() {
    return this.#http.get(`${STUDIO}/packages`);
  }

  async getPackage(packageId) {
    return this.#http.get(`${STUDIO}/packages/${packageId}`);
  }

  async createPackage(data) {
    return this.#http.post(`${STUDIO}/packages`, data);
  }

  async updatePackage(packageId, data) {
    return this.#http.put(`${STUDIO}/packages/${packageId}`, data);
  }

  async deletePackage(packageId) {
    return this.#http.delete(`${STUDIO}/packages/${packageId}`);
  }

  // --- Methods ---

  async getMethods(packageId) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/methods`);
  }

  async getMethod(packageId, methodId) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/methods/${methodId}`);
  }

  async createMethod(packageId, data) {
    return this.#http.post(`${STUDIO}/packages/${packageId}/methods`, data);
  }

  async updateMethod(packageId, methodId, data) {
    return this.#http.put(`${STUDIO}/packages/${packageId}/methods/${methodId}`, data);
  }

  async deleteMethod(packageId, methodId) {
    return this.#http.delete(`${STUDIO}/packages/${packageId}/methods/${methodId}`);
  }

  // --- Credentials (write-only; value never returned) ---

  async getCredentials(packageId, config) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/credentials`, config);
  }

  async setCredential(packageId, credentialKey, environment, value) {
    return this.#http.post(`${STUDIO}/packages/${packageId}/credentials`, {
      credentialKey,
      environment,
      value,
    });
  }

  async rotateCredential(packageId, credentialId) {
    return this.#http.post(`${STUDIO}/packages/${packageId}/credentials/${credentialId}/rotate`);
  }

  // --- Constants (plain: value returned + inline-editable; secret: write-only, masked/last-4 + rotate) ---

  async getConstants(packageId, config) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/constants`, config);
  }

  async setConstant(packageId, constant) {
    return this.#http.post(`${STUDIO}/packages/${packageId}/constants`, constant);
  }

  async deleteConstant(packageId, constantId) {
    return this.#http.delete(`${STUDIO}/packages/${packageId}/constants/${constantId}`);
  }

  async rotateConstant(packageId, constantId, value) {
    return this.#http.post(`${STUDIO}/packages/${packageId}/constants/${constantId}/rotate`, {
      value,
    });
  }

  // --- Allowed hosts (SSRF allowlist) ---

  async getAllowedHosts(packageId) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/allowed-hosts`);
  }

  async addAllowedHost(packageId, hostPattern, justification) {
    return this.#http.post(`${STUDIO}/packages/${packageId}/allowed-hosts`, {
      hostPattern,
      justification,
    });
  }

  async removeAllowedHost(packageId, allowedHostId) {
    return this.#http.delete(`${STUDIO}/packages/${packageId}/allowed-hosts/${allowedHostId}`);
  }

  async approveAllowedHost(packageId, allowedHostId) {
    return this.#http.post(
      `${STUDIO}/packages/${packageId}/allowed-hosts/${allowedHostId}/approve`,
    );
  }

  // --- Method execution (dry-run + live test) ---
  // Via LoanProcessing's test proxy so the loan context is resolved (executor-passes-context) before the
  // egress call; dryRun suppresses write-back of the response onto the loan.

  // environment (HZ-4015): the IntegrationEnvironment name to dispatch against; null/omitted lets the
  // test proxy inherit the deployment tier.
  async dryRunMethod(packageId, methodId, applicationId, environment = null) {
    return this.#loanProcessingHttp.post('/api/integration-methods/execute', {
      packageId,
      methodId,
      applicationId,
      environment,
      dryRun: true,
    });
  }

  async testMethod(packageId, methodId, applicationId, environment = null) {
    return this.#loanProcessingHttp.post('/api/integration-methods/execute', {
      packageId,
      methodId,
      applicationId,
      environment,
      dryRun: false,
    });
  }

  // --- Mappings (request-body spec + response field mappings) ---

  async getMappings(packageId, methodId) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/methods/${methodId}/mappings`);
  }

  async saveMappings(packageId, methodId, body) {
    return this.#http.put(`${STUDIO}/packages/${packageId}/methods/${methodId}/mappings`, body);
  }

  // --- Run history (immutable invocation audit log) ---

  async getRunHistory(params) {
    return this.#http.get(`${STUDIO}/run-history`, { params });
  }

  async getRunHistoryForMethod(packageId, methodId, params) {
    return this.#http.get(`${STUDIO}/packages/${packageId}/methods/${methodId}/run-history`, {
      params,
    });
  }
}
